import * as React from "react";
import { Link, useLocation, useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { http } from "@/app/services/api";
import { useLanguage } from "@/app/contexts/LanguageContext";
import { pick } from "@/app/utils/mlField";
import { FieldHint } from "@/app/components/ui/field-hint";
import { ROUTES } from "@/app/config/routes";
import type { MainLayoutContext } from "@/app/layouts/MainLayout";
import {
  AppOverviewCard,
  CurateSection,
  VersionsSection,
} from "@/app/pages/AppDetailPageSections";
import type { AdminAppDetail } from "@/app/types/api";

export function AppDetailPage() {
  const { appId = "" } = useParams<{ appId: string }>();
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const layout = useOutletContext<MainLayoutContext | undefined>();

  const previewFromState = (location.state as
    | {
        appPreview?: {
          app_id: string;
          name: string;
          latest_version: string | null;
          category: string | null;
        };
      }
    | undefined)?.appPreview;
  const previewFromCache =
    queryClient
      .getQueryData<{ items: Array<{ app_id: string; name: string; latest_version: string | null; category: string | null }> }>([
        "admin",
        "apps",
        lang,
      ])
      ?.items?.find((item) => item.app_id === appId) ?? null;

  const detail = useQuery({
    queryKey: ["admin", "app", appId],
    queryFn: () =>
      http.get<AdminAppDetail>(
        `/api/v1/admin/apps/${encodeURIComponent(appId)}`
      ),
    enabled: !!appId,
  });

  const detailData = detail.data;
  const latest =
    (detailData?.versions || []).find((v) => v.is_latest) ||
    (detailData?.versions || [])[0] ||
    null;
  const preview = previewFromState ?? previewFromCache;
  const displayName = detailData
    ? pick(detailData.name, lang) || detailData.app_id
    : preview?.name || "";
  const topbarTitle = React.useMemo(
    () => (
      <>
        <Link to={ROUTES.apps} className="hover:text-primary underline-offset-2 hover:underline">
          {t("detail.registeredAppsRoot")}
        </Link>
        {displayName ? <span>{` > ${displayName}`}</span> : null}
      </>
    ),
    [displayName, t]
  );
  const topbarSub = detailData
    ? `${displayName} · ${detailData.app_id}${latest ? ` · v${latest.version}` : ""}${detailData.category ? ` · ${detailData.category}` : ""}`
    : preview
      ? `${preview.name} · ${preview.app_id}${preview.latest_version ? ` · v${preview.latest_version}` : ""}${preview.category ? ` · ${preview.category}` : ""}`
      : t("view.apps.sub");

  React.useEffect(() => {
    if (!detailData && !preview) {
      layout?.setHeaderMeta?.(null);
      return;
    }

    layout?.setHeaderMeta?.({
      title: topbarTitle,
      sub: topbarSub,
    });

    return () => layout?.setHeaderMeta?.(null);
  }, [detailData, layout, topbarSub, topbarTitle]);

  if (detail.isLoading) {
    return <FieldHint>{t("apps.loading")}</FieldHint>;
  }
  if (detail.isError || !detail.data) {
    return (
      <p className="text-sm text-destructive">
        {t("apps.error", { error: (detail.error as Error)?.message ?? "" })}
      </p>
    );
  }

  const d = detail.data;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.4fr)]">
        <AppOverviewCard d={d} />

        <VersionsSection
          d={d}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ["admin", "app", appId] });
            queryClient.invalidateQueries({ queryKey: ["admin", "apps"] });
          }}
          onAddVersion={() => {
            navigate(ROUTES.upload, {
              state: { forApp: { app_id: d.app_id, name: displayName } },
            });
          }}
        />
      </div>

      <CurateSection
        d={d}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey: ["admin", "app", appId] });
          queryClient.invalidateQueries({ queryKey: ["admin", "apps"] });
        }}
      />
    </div>
  );
}
